<svelte:options tag={null} />

<script lang="ts">
  import { onDestroy } from 'svelte';
  import Segment from './sidebar-segment.nested.svelte';
  import Section from './sidebar-section.nested.svelte';
  import NavigationUser from './sidebar-user.nested.svelte';
  import NavigationSeparator from './sidebar-separator.nested.svelte';
  import { EventHandler } from '../../utilities/EventHandler';
  import {
    selectFeature,
    updateSidebarData,
    toggleSidebar,
    isSidebarOpen,
    sidebar,
    selectedFeature,
  } from './sidebar-service';
  import type { Unsubscriber } from 'svelte/store';
  import type { A11yLabels, Sidebar, User } from './sidebar.types';

  export let data: string | Sidebar;
  export let open: boolean;
  export let code: string;
  export let user: string | User;
  export let a11yLabels: A11yLabels;
  let eventHandler = new EventHandler();
  let parsedUser: User;
  let previousCode: string;
  let previousData: string | Sidebar;
  let previousOpen = false;

  $: updateData(code, data);
  $: updateOpen(open);
  $: parsedUser = typeof user === 'string' ? (JSON.parse(user) as User) : user;
  $: attributes = { ...$$restProps };
  $: delete attributes.class;

  function updateData(newCode: string, newData: string | Sidebar) {
    if (typeof previousData !== typeof newData || previousData !== newData) {
      previousData = newData;
      updateSidebarData(newData);
    }
    if (typeof previousCode !== typeof newCode || previousCode !== newCode) {
      previousCode = newCode;
      selectFeature(newCode);
    }
  }

  function updateOpen(newOpenStatus?: boolean) {
    if (
      typeof previousOpen !== typeof newOpenStatus ||
      previousOpen !== newOpenStatus
    ) {
      previousOpen = Boolean(newOpenStatus);
      toggleSidebar(Boolean(newOpenStatus));
    }
  }

  const unsubscribes: Unsubscriber[] = [];
  unsubscribes.push(
    isSidebarOpen.subscribe((state) => {
      eventHandler.dispatch('on-toggle', state);
    }),
  );

  unsubscribes.push(
    selectedFeature.subscribe((selection) => {
      // Emit an event and close the menu when the selection changes.
      if (selection) {
        toggleSidebar(false);
        eventHandler.dispatch(
          'on-select',
          selection.lv2?.code ?? selection.lv1?.code ?? selection.lv0?.code,
        );
      }
    }),
  );

  onDestroy(() => {
    for (const unsubscribe of unsubscribes) {
      unsubscribe();
    }
  });
</script>

<nav
  class="mc-sidebar {$isSidebarOpen ? 'is-open' : 'is-close'}"
  aria-expanded={$isSidebarOpen}
  aria-labelledby="mc-sidebar__trigger"
  {...attributes}
>
  <div class="mc-sidebar__container {parsedUser ? 'has-bottom-panel' : ''}">
    <button
      id="mc-sidebar__trigger"
      class="mc-sidebar__trigger"
      aria-label={$isSidebarOpen
        ? a11yLabels?.['close'] || 'Close the sidebar'
        : a11yLabels?.['open'] || 'Open the sidebar'}
      on:click={() => toggleSidebar()}
    >
      <span class="mc-sidebar__trigger__container">
        {#if $isSidebarOpen}
          <svg width="24px" height="24px" viewBox="0 0 24 24"
            ><path
              d="M10.707 5.707a1 1 0 00-1.414-1.414l-7 7a1 1 0 000 1.414l7 7a1 1 0 001.414-1.414L4.414 12l6.293-6.293zm9 0a1 1 0 00-1.414-1.414l-7 7a1 1 0 000 1.414l7 7a1 1 0 001.414-1.414L13.414 12l6.293-6.293z"
            /></svg
          >
        {:else}
          <svg width="24px" height="24px" viewBox="0 0 24 24"
            ><path
              d="M5.707 4.293a1 1 0 00-1.414 1.414L10.586 12l-6.293 6.293a1 1 0 101.414 1.414l7-7a1 1 0 000-1.414l-7-7zm9 0a1 1 0 10-1.414 1.414L19.586 12l-6.293 6.293a1 1 0 001.414 1.414l7-7a1 1 0 000-1.414l-7-7z"
            /></svg
          >
        {/if}
      </span>
    </button>
    <Segment data={$sidebar?.HOME} />
    <NavigationSeparator />
    <Section data={$sidebar?.FEATURES} {a11yLabels} />
    {#if $sidebar?.UTILITIES}
      <div class="mc-sidebar__utilities">
        {#each $sidebar?.UTILITIES as section}
          <NavigationSeparator />
          <ul>
            {#each section as feature}
              <li class="mc-sidebar__utilities__item">
                <Segment data={feature} />
              </li>
            {/each}
          </ul>
        {/each}
      </div>
    {/if}
  </div>
  {#if parsedUser}
    <NavigationUser
      user={parsedUser}
      data={$sidebar?.USER_FEATURES}
      {a11yLabels}
    />
  {/if}
</nav>

<style lang="scss">
  @import './sidebar-variables';

  .mc-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    font-size: 14px;
    color: white;
    box-sizing: border-box;
    fill: white;
    width: 320px;
    background: #082435;
    display: block;
    height: 100vh;

    &__container {
      height: 100%;
      display: flex;
      flex-direction: column;
      &.has-bottom-panel {
        height: calc(100% - 88px);
      }
    }

    &__utilities {
      margin-bottom: 24px;

      &__item {
        margin: 4px 0px;
        &:first-child {
          margin-top: 0;
        }
        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__trigger {
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
      min-height: 92px;
      width: 100%;
      font-size: inherit;
      font-family: inherit;
      position: relative;
      display: flex;

      &__container {
        position: relative;
        margin: 28px 20px 28px 20px;
        height: 32px;
        width: 100%;
        & > * {
          position: absolute;
          top: 0px;
          right: 0px;
          width: 24px;
          height: 24px;
        }
      }

      &:focus-visible {
        box-shadow: none;
        border: none;
        outline: none;

        & .mc-sidebar__trigger__container {
          box-shadow: none;
          border: none;
          border-radius: 6px;
          outline: 1px solid #0b96cc;
        }
      }
    }

    &.is-open {
      @include transition(width 300ms ease-in-out);
    }

    &.is-close {
      @include transition(width 300ms ease-in-out);

      width: 64px;
      padding: 0;
    }

    *::-webkit-scrollbar {
      width: 5px;
      height: 10px;
      background: #082435;
    }

    *::-webkit-scrollbar-thumb {
      background: #009eac;
    }

    &::-webkit-scrollbar {
      width: 5px;
      height: 10px;
      background: #082435;
    }

    &::-webkit-scrollbar-thumb {
      background: #009eac;
    }

    ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }
  }
</style>
